import React, { useState, useCallback, createContext, useContext } from 'react';
import { useNavigate, Outlet } from 'react-router-dom';
import { toast } from 'sonner';
import AppSidebar from './AppSidebar';
import webChatHistoryService from '../services/webChatHistoryService';
import { useAuth } from '../contexts/AuthContext';
import { useRISearch } from '../contexts/RISearchContext';

// Maximum concurrent searches allowed
const MAX_CONCURRENT_SEARCHES = 5;

interface AppLayoutContextType {
  currentSessionId: string | null;
  setCurrentSessionId: (id: string | null) => void;
  triggerSidebarRefresh: () => void;
  sidebarRefreshTrigger: number;
  onNewChat: () => Promise<string>;
  onSessionSelect: (sessionId: string) => void;
  pendingSessionLoad: { sessionId: string } | null;
  clearPendingSessionLoad: () => void;
}

const AppLayoutContext = createContext<AppLayoutContextType | undefined>(undefined);

export function useAppLayout(): AppLayoutContextType {
  const context = useContext(AppLayoutContext);
  if (!context) {
    throw new Error('useAppLayout must be used within an AppLayout');
  }
  return context;
}

const AppLayout: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeSearchCount } = useRISearch();

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [sidebarRefreshTrigger, setSidebarRefreshTrigger] = useState(0);
  const [pendingSessionLoad, setPendingSessionLoad] = useState<{ sessionId: string } | null>(null);

  const triggerSidebarRefresh = useCallback(() => {
    setSidebarRefreshTrigger(prev => prev + 1);
  }, []);

  const clearPendingSessionLoad = useCallback(() => {
    setPendingSessionLoad(null);
  }, []);

  const handleNewChat = useCallback(async (): Promise<string> => {
    // Check rate limit for parallel searches
    if (activeSearchCount >= MAX_CONCURRENT_SEARCHES) {
      toast.warning(`Maximale Anzahl paralleler Suchen erreicht (${MAX_CONCURRENT_SEARCHES}). Bitte warten Sie.`, {
        duration: 3000,
      });
      return currentSessionId || '';
    }

    // Generate new session ID
    const newSessionId = await webChatHistoryService.generateSessionId('ris', user?.id);
    setCurrentSessionId(newSessionId);
    navigate('/ris');
    return newSessionId;
  }, [navigate, user?.id, activeSearchCount, currentSessionId]);

  const handleSessionSelect = useCallback((sessionId: string) => {
    // Allow session switching at any time (user may want to view other sessions)
    setCurrentSessionId(sessionId);
    setPendingSessionLoad({ sessionId });
    navigate('/ris');
  }, [navigate]);

  const contextValue: AppLayoutContextType = {
    currentSessionId,
    setCurrentSessionId,
    triggerSidebarRefresh,
    sidebarRefreshTrigger,
    onNewChat: handleNewChat,
    onSessionSelect: handleSessionSelect,
    pendingSessionLoad,
    clearPendingSessionLoad,
  };

  return (
    <AppLayoutContext.Provider value={contextValue}>
      <div className="min-h-screen h-screen flex bg-warm-gradient">
        <AppSidebar
          currentSessionId={currentSessionId}
          onSessionSelect={handleSessionSelect}
          onNewChat={handleNewChat}
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          refreshTrigger={sidebarRefreshTrigger}
        />
        <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
          <Outlet />
        </main>
      </div>
    </AppLayoutContext.Provider>
  );
};

export default AppLayout;
